import { useMemo } from "react";
import katex from "katex";
import "katex/dist/katex.min.css";

interface MathTextProps {
  content: string;
  className?: string;
}

// Regex patterns for math expressions
// Display math: $$...$$ or \[...\]
const DISPLAY_MATH_REGEX = /\$\$([^$]+)\$\$|\\\[([^\]]+)\\\]/g;
// Inline math: $...$ or \(...\)
const INLINE_MATH_REGEX = /\$([^$\n]+)\$|\\\(([^)]+)\\\)/g;
// Bold text: **...**
const BOLD_REGEX = /\*\*([^*]+)\*\*/g;

interface TextSegment {
  type: "text" | "inline-math" | "display-math" | "bold";
  content: string;
}

/**
 * Parse content into segments of text, math, and formatting
 */
function parseContent(content: string): TextSegment[] {
  const segments: TextSegment[] = [];
  let remaining = content;

  while (remaining.length > 0) {
    // Find the earliest match of any pattern
    const displayMatch = DISPLAY_MATH_REGEX.exec(remaining);
    DISPLAY_MATH_REGEX.lastIndex = 0; // Reset regex state

    const inlineMatch = INLINE_MATH_REGEX.exec(remaining);
    INLINE_MATH_REGEX.lastIndex = 0;

    const boldMatch = BOLD_REGEX.exec(remaining);
    BOLD_REGEX.lastIndex = 0;

    // Find earliest match
    const matches = [
      displayMatch
        ? { match: displayMatch, type: "display-math" as const }
        : null,
      inlineMatch ? { match: inlineMatch, type: "inline-math" as const } : null,
      boldMatch ? { match: boldMatch, type: "bold" as const } : null,
    ].filter(Boolean) as {
      match: RegExpExecArray;
      type: TextSegment["type"];
    }[];

    if (matches.length === 0) {
      // No more patterns, add remaining as text
      if (remaining) {
        segments.push({ type: "text", content: remaining });
      }
      break;
    }

    // Sort by position
    matches.sort((a, b) => a.match.index - b.match.index);
    const earliest = matches[0];

    // Add text before the match
    if (earliest.match.index > 0) {
      segments.push({
        type: "text",
        content: remaining.slice(0, earliest.match.index),
      });
    }

    // Add the matched content
    // For display math: either group 1 ($$) or group 2 (\[)
    // For inline math: either group 1 ($) or group 2 (\()
    // For bold: group 1
    const matchedContent =
      earliest.match[1] || earliest.match[2] || earliest.match[0];

    segments.push({
      type: earliest.type,
      content: matchedContent,
    });

    // Move past the match
    remaining = remaining.slice(
      earliest.match.index + earliest.match[0].length,
    );
  }

  return segments;
}

/**
 * Render KaTeX math expression safely
 */
function renderMath(latex: string, displayMode: boolean): string {
  try {
    return katex.renderToString(latex, {
      displayMode,
      throwOnError: false,
      errorColor: "#ef4444",
      strict: false,
      trust: false,
    });
  } catch {
    // If KaTeX fails, return the original with error styling
    return `<span class="text-red-500">${latex}</span>`;
  }
}

/**
 * Component that renders text with math expressions (KaTeX) and basic formatting
 */
export function MathText({ content, className = "" }: MathTextProps) {
  const rendered = useMemo(() => {
    const segments = parseContent(content);

    return segments.map((segment, index) => {
      switch (segment.type) {
        case "display-math":
          return (
            <div
              key={index}
              className="my-2 overflow-x-auto"
              dangerouslySetInnerHTML={{
                __html: renderMath(segment.content, true),
              }}
            />
          );
        case "inline-math":
          return (
            <span
              key={index}
              dangerouslySetInnerHTML={{
                __html: renderMath(segment.content, false),
              }}
            />
          );
        case "bold":
          return (
            <strong key={index} className="font-semibold">
              {segment.content}
            </strong>
          );
        case "text":
        default:
          return <span key={index}>{segment.content}</span>;
      }
    });
  }, [content]);

  return (
    <div
      className={`whitespace-pre-wrap break-words leading-relaxed ${className}`}
    >
      {rendered}
    </div>
  );
}
